import _toConsumableArray from 'babel-runtime/helpers/toConsumableArray';
import { Base64 } from 'js-base64';
import get from 'lodash.get';

/**
 * Get Decoded ID
 *
 * the decoded ID looks like => gid://shopify/Product/10328926858
 * splitting by / and returning the last item gives you the id
 * Can work with product IDs plus variant IDs
 *
 * @param {string} id - base64 ID from storefront api
 * @return {string} decoded ID - returns decoded ID
 */
export var getDecodedId = function getDecodedId(id) {
  return Base64.decode(id).split('/').pop();
};

/**
 * Get Encoded ID
 *
 * DY gives us the Variant ID string => '1669710577684'
 * We need to encode the variant id to base64
 * so we can add the variant to cart
 *
 * @param {string} id - DY id (string of numbers)
 * @return {string} Encoded ID - returns encoded ID
 */
export var getEncodedProductId = function getEncodedProductId(id) {
  var formattedId = 'gid://shopify/Product/' + id;
  return Base64.encode(formattedId);
};

/**
 * Get ProductHref
 * @param  {String} productHandle
 * @param  {String} selectedVariantId
 * @return  {String} productHref with variant param
 */
export var getProductHref = function getProductHref(productHandle, selectedVariantId) {
  var decodedVariantId = parseInt(getDecodedId(selectedVariantId));
  return '/products/' + productHandle + '?variant=' + decodedVariantId;
};

/**
 * Get Selected Option Values Ary
 *
 * Loop through selected options and return its values
 * @param {Array} selectedOptions- selectedOptions
 * @return {Array} return array of selected option values
 */
export var getSelectedOptionValuesAry = function getSelectedOptionValuesAry(selectedOptions) {
  return selectedOptions.map(function (option) {
    return option.value.toLowerCase();
  });
};

/**
 * Is Gallery Image ?
 *
 * Check if Image should be included in Gallery. Criteria
 * 1. If altText is empty, this is an alt image and should always be displayed
 * 2. If image is the selectedVariant image, this should be shown since it matches selected variant image
 * 3. If image alt text matches one of the selected variant options.
 *    i.e. alt text = purple and selected option values are ['purple', 'twin']
 *
 * @param {Object} image- image object
 * @param {sting} selectedVariantImage - selectedVariantImageSrc
 * @param {Array} selectedOptionValuesAry - Ary of selected option values
 * @return {Boolean} isGallery image Boolean
 */
export var isGalleryImage = function isGalleryImage(image, selectedVariantImage, selectedOptionValuesAry) {
  return image.node.altText === null || image.node.productImg === selectedVariantImage || selectedOptionValuesAry.includes(image.node.altText.toLowerCase());
};

export var getAccordionReturnContent = function getAccordionReturnContent(customReturnPolicy, copy) {
  if (customReturnPolicy && customReturnPolicy.value) {
    return customReturnPolicy.value;
  } else {
    // Holiday return policy copy
    // return `<h6>Holiday Return Policy</h6><p>This item is eligible for an easy, hassle-free return within 90 days from the date of purchase. All returns will be refunded to the original form of payment.</p>`;

    return copy;
  }
};

/*** CHECK IF VALID EMAIL ***/
export var isValidEmailAddress = function isValidEmailAddress(emailAddress) {
  var pattern = new RegExp(/^(("[\w-\s]+")|([\w-]+(?:\.[\w-]+)*)|("[\w-\s]+")([\w-]+(?:\.[\w-]+)*))(@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$)|(@\[?((25[0-5]\.|2[0-4][0-9]\.|1[0-9]{2}\.|[0-9]{1,2}\.))((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\.){2}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\]?$)/i);
  return pattern.test(emailAddress);
};

/***
 * Creates obj to map over
 * when making accordion on PDPs.
 */

var makeContentArray = function makeContentArray(c1, c2) {
  var cArr = [];
  if (c1 !== undefined && c1 !== null) cArr.push(c1);
  if (c2 !== undefined && c2 !== null) cArr.push(c2);

  return cArr;
};

/***
 * Insert elements in array
 * if condition is true
 */
var insertIf = function insertIf(condition) {
  for (var _len = arguments.length, elements = Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    elements[_key - 1] = arguments[_key];
  }

  return condition ? elements : [];
};

/**
 *
 * Get value from metafield util
 */

var getValueFromMetafields = function getValueFromMetafields(metafield) {
  return get(metafield, 'value', null);
};

export var makeAccordionObj = function makeAccordionObj(product, selectedVariant, shippingTimes, returnCopy, shippingSurcharge) {
  var whyWeLove = getValueFromMetafields(product.whyWeLove);
  var expertTips = getValueFromMetafields(product.expertTips);
  var material = getValueFromMetafields(selectedVariant.material);
  var dimensions = getValueFromMetafields(selectedVariant.dimension);
  var care = getValueFromMetafields(product.care);
  var careLongText = getValueFromMetafields(product.careLongText);
  var returnPolicy = product.returnPolicy ? getValueFromMetafields(product.returnPolicy) : returnCopy;
  var vendorShipping = renderShippingMessage(product.vendor, shippingTimes);

  var tipsArr = makeContentArray(whyWeLove, expertTips);
  var detailsArr = makeContentArray(material, dimensions);
  var careArr = makeContentArray(care, careLongText);
  var shippingReturnsArr = makeContentArray(vendorShipping, returnPolicy);

  if (shippingSurcharge) {
    shippingReturnsArr.unshift('Due to the size and weight of this product, it needs a little extra care. An additional shipping cost will be incurred at checkout.');
  }

  return [].concat(_toConsumableArray(insertIf(tipsArr.length > 0, {
    title: 'Why You Need This',
    content: tipsArr
  })), _toConsumableArray(insertIf(detailsArr.length > 0, {
    title: 'Details',
    content: detailsArr
  })), _toConsumableArray(insertIf(careArr.length > 0, {
    title: 'Care',
    content: careArr
  })), _toConsumableArray(insertIf(shippingReturnsArr.length > 0, {
    title: 'Shipping & Returns',
    content: shippingReturnsArr
  })));
};

/***
 * Get Out of Stock Span
 */

export var getOutOfStockSpan = function getOutOfStockSpan(preorderData, productPreOrderData, selectedVariantInventory, lowInStockThreshold, outOfStock) {
  if (preorderData && preorderData.isInFuture) {
    return 'Item will ship by  ' + preorderData.string;
  } else if (productPreOrderData && productPreOrderData.isInFuture) {
    return 'Item will ship by  ' + productPreOrderData.string;
  } else if (outOfStock) {
    return 'Sold Out';
  } else if (selectedVariantInventory <= lowInStockThreshold && selectedVariantInventory > 0) {
    return 'Hurry! Only ' + selectedVariantInventory + ' left!';
  } else {
    return null;
  }
};

/***
 * Render Shipping Times
 *
 * Displays shipping times for Product Accordion
 * Based on vendor/tags. Message is set in Theme Customization
 */

export var renderShippingMessage = function renderShippingMessage(vendor, shippingTimes) {
  switch (vendor) {
    case 'Virtual':
      return 'Delivered Immediately';
    case 'Radial':
    case 'Dormify Doing Good':
    case 'Sleepyhead':
      return 'Ships in ' + shippingTimes.defaultShipping + ' Business Days.';
    case 'Photogra':
      return 'Ships in ' + shippingTimes.photograShipping + ' Business Days.';
    case 'Cheyenne':
      return 'Ships in ' + shippingTimes.cheyenneShipping + ' Business Days.';
    case 'Skyline Furniture':
      return 'Ships in ' + shippingTimes.skylineFurnitureShipping + ' Weeks.';
    case 'Elegant Linen':
    case '48 Hour Monogram':
      return 'Ships in ' + shippingTimes.customShipping + ' Business Days.';
    default:
      return 'Ships in ' + shippingTimes.nonWarehouseShipping + ' Business Days.';
  }
};

/**
 * Get Vendor Name
 *
 * Displays Vendor from vendor_for_display metafield.
 * If not available, display standard vendor.
 */

export var getVendorName = function getVendorName(vendorForDisplay, vendor) {
  return vendorForDisplay ? getValueFromMetafields(vendorForDisplay) : vendor;
};